import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Play, Pause, SkipForward, SkipBack, X, Presentation } from 'lucide-react';

const demoSteps = [
  { path: '/dashboard', title: 'Command Center', narration: 'CISO-level situational awareness. AI posture score, regulatory readiness, and live decision feed — all in one view.' },
  { path: '/dashboard/regulatory-moment', title: 'Regulatory Moment of Truth', narration: 'The core product story: what happens when a regulator asks for AI decision evidence. Without Apex: days of assembly, incomplete. With Apex: 90 seconds, cryptographically signed.' },
  { path: '/dashboard/decision-ledger', title: 'Decision Ledger', narration: '47,832 tamper-evident decision records. Every AI invocation captured, signed, and mapped to compliance frameworks in real-time.' },
  { path: '/dashboard/decision-ledger/DER-2026-047832', title: 'Decision Evidence Record', narration: 'A single record: model, input, output, data sources, regulatory controls, and an immutable chain of custody — all generated in 960ms.' },
  { path: '/dashboard/evidence-vault', title: 'Evidence Vault', narration: 'Continuous integrity verification. 47,614 records verified. 7-year retention. Signed integrity manifests on demand.' },
  { path: '/dashboard/risk-score', title: 'Risk Score', narration: 'Board-ready posture scoring. 74/100 with peer benchmarking against regulated enterprises. Remediation roadmap built in.' },
  { path: '/dashboard/lineage', title: 'Data Lineage', narration: 'End-to-end provenance tracking. Every data source, processing step, and endpoint — with trust scores and unverified dependency detection.' },
];

const STEP_DURATION = 10000;

export default function DemoMode() {
  const [active, setActive] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [progress, setProgress] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();

  const goToStep = useCallback((idx) => {
    const step = demoSteps[idx];
    if (step) {
      setCurrentStep(idx);
      setProgress(0);
      navigate(step.path);
    }
  }, [navigate]);

  useEffect(() => {
    if (!active || !playing) return;
    const interval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 100) {
          const next = (currentStep + 1) % demoSteps.length;
          goToStep(next);
          return 0;
        }
        return prev + (100 / (STEP_DURATION / 100));
      });
    }, 100);
    return () => clearInterval(interval);
  }, [active, playing, currentStep, goToStep]);

  const start = () => {
    setActive(true);
    setPlaying(true);
    setCurrentStep(0);
    setProgress(0);
    navigate(demoSteps[0].path);
  };

  const stop = () => {
    setActive(false);
    setPlaying(false);
    setCurrentStep(0);
    setProgress(0);
  };

  if (!active) {
    return (
      <button
        data-testid="demo-mode-btn"
        onClick={start}
        className="fixed bottom-5 right-5 z-50 flex items-center gap-2 bg-[#6366F1] hover:bg-[#818cf8] text-white text-xs font-semibold px-4 py-2.5 rounded-full transition-all shadow-[0_0_30px_rgba(99,102,241,0.4)] hover:shadow-[0_0_40px_rgba(99,102,241,0.6)] hover:-translate-y-0.5"
      >
        <Presentation size={14} /> Live Demo
      </button>
    );
  }

  const step = demoSteps[currentStep];

  return (
    <div data-testid="demo-mode-bar" className="fixed bottom-0 left-0 right-0 z-50">
      {/* Progress bar */}
      <div className="h-0.5 bg-[#1E2A45]">
        <div className="h-full bg-[#6366F1] transition-all duration-100" style={{ width: `${progress}%` }} />
      </div>
      {/* Control bar */}
      <div className="bg-[#0F1629]/95 backdrop-blur-lg border-t border-[#1E2A45] px-4 sm:px-6 py-3">
        <div className="flex items-center gap-4 max-w-6xl mx-auto">
          {/* Controls */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <button onClick={() => goToStep((currentStep - 1 + demoSteps.length) % demoSteps.length)} className="p-1.5 rounded-lg hover:bg-[#1E2A45] text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
              <SkipBack size={14} />
            </button>
            <button
              data-testid="demo-play-pause"
              onClick={() => setPlaying(!playing)}
              className="p-2 rounded-full bg-[#6366F1] text-white hover:bg-[#818cf8] transition-colors"
            >
              {playing ? <Pause size={14} /> : <Play size={14} />}
            </button>
            <button onClick={() => goToStep((currentStep + 1) % demoSteps.length)} className="p-1.5 rounded-lg hover:bg-[#1E2A45] text-[#94A3B8] hover:text-[#F8FAFC] transition-colors">
              <SkipForward size={14} />
            </button>
          </div>

          {/* Step dots */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {demoSteps.map((_, i) => (
              <button key={i} onClick={() => goToStep(i)} className={`w-2 h-2 rounded-full transition-all ${i === currentStep ? 'bg-[#6366F1] scale-125' : i < currentStep ? 'bg-[#6366F1]/40' : 'bg-[#1E2A45]'}`} />
            ))}
          </div>

          {/* Narration */}
          <div className="flex-1 min-w-0 mx-2">
            <div className="text-[10px] text-[#6366F1] uppercase tracking-wider font-semibold mb-0.5">
              {currentStep + 1}/{demoSteps.length} · {step.title}
            </div>
            <div className="text-xs text-[#94A3B8] truncate">{step.narration}</div>
          </div>

          {/* Close */}
          <button data-testid="demo-close" onClick={stop} className="p-1.5 rounded-lg hover:bg-[#1E2A45] text-[#475569] hover:text-[#F8FAFC] transition-colors flex-shrink-0">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
